import React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect, ConnectedProps } from 'react-redux';

import { AppState } from '../../stores/redux-store';
import { messages as authorizationMessages } from '../AuthorizationDirector/messages';
import { SuccessNotification } from '../../core/screens/SuccessNotification/SuccessNotification';
import { FlowFinished as FlowFinishedImport } from '../screens/FlowFinished/FlowFinished';
import { useConfiguration } from '../ApplicationProviders/ConfigurationProvider/ConfigurationProvider';
import { useSelectedProvider } from '../App/ProviderContainer/useSelectedProvider';
import { CredentialsResult } from '../CredentialsDirector/CredentialsResult';
import { Credentials } from '../../models/credentials';
import { useIsFlowHandledByRedirectHandler as useIsFlowHandledByRedirectHandlerImport } from '../../utilities/useIsFlowHandledByRedirectHandler';
import { applicationParameters } from '../../utilities/application-parameters';
import { UpdateCredentialsConsent } from './molecules/UpdateCredentialsConsent/UpdateCredentialsConsent';

type UpdateConsentDirectorProps = PropsFromRedux & {
  useIsFlowHandledByRedirectHandler?: typeof useIsFlowHandledByRedirectHandlerImport;
  FlowFinished?: typeof FlowFinishedImport;
};

export const UpdateConsentDirectorPresenter: React.FC<UpdateConsentDirectorProps> = ({
  credentials,
  redirectedCredentialsId,
  useIsFlowHandledByRedirectHandler = useIsFlowHandledByRedirectHandlerImport,
  FlowFinished = FlowFinishedImport,
}) => {
  const { selectedProvider } = useSelectedProvider();

  const { data: configurationData } = useConfiguration();

  const credentialsUpdated = Credentials.isFinished(credentials, configurationData);

  const flowContinuesInRedirectHandler = useIsFlowHandledByRedirectHandler({
    redirectedCredentialsId,
    appUri: applicationParameters.get('appUri'),
    credentials,
  });

  if (flowContinuesInRedirectHandler && credentialsUpdated) {
    return <FlowFinished />;
  }

  if (credentialsUpdated) {
    const message = (
      <FormattedMessage
        {...authorizationMessages.accountConnectedTo}
        values={{ providerName: selectedProvider.displayName }}
      />
    );

    const applicationSuccessResult = new CredentialsResult({
      credentialsId: credentials.id,
    });

    return (
      <SuccessNotification
        overlayHeading={message}
        applicationSuccessResult={applicationSuccessResult}
      />
    );
  }

  return <UpdateCredentialsConsent />;
};

const mapStateToProps = (state: AppState) => {
  return {
    credentials: state.credentials.data,
    redirectedCredentialsId: state.application.redirectedCredentialsId,
  };
};

const connector = connect(mapStateToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export const UpdateConsentDirector = connector(UpdateConsentDirectorPresenter);
